<template>
  <div class="component">
    <div>
      <div class="vertical-pane">
        <div id="instruction-set-editor">
          <h1>{{ scopedStrings.titles.instructionSetEditor }}</h1>

          <table>
            <tr>
              <th
                  v-for="attribute in scopedStrings.attributes"
                  :key="attribute.name"
              >
                {{ attribute.name }}
              </th>
            </tr>
            <tr v-for="(instruction, index) in currentInstructionSet" :key="index">
              <td>
                <span v-if="index < defaultInstructionsCount">
                  {{ instruction.name }}
                </span>
                <input
                    :placeholder="scopedStrings.required"
                    v-else
                    type="text"
                    v-model.trim="instruction.name"
                />
              </td>
              <td>
                <input
                    type="number"
                    min="0"
                    max="15"
                    v-model.number="instruction.icode"
                />
              </td>
              <td>
                <input
                    type="number"
                    min="0"
                    max="15"
                    v-model.number="instruction.ifun"
                />
              </td>
              <td>
                <input
                    type="text"
                    v-model="instruction.args"
                />
              </td>
              <div class="remove-instruction">
                <button
                    v-if="index >= defaultInstructionsCount"
                    @click="currentInstructionSet.splice(index, 1)"
                >
                  <i class="fa fa-times"></i>
                </button>
              </div>
            </tr>
            <tr>
              <td colspan="6">
                <button id="new-instruction" @click="newInstruction()">
                  {{ scopedStrings.buttons.newInstruction }}
                </button>
              </td>
            </tr>
          </table>
        </div>
        <div id="documentation">
          <h1>{{ scopedStrings.titles.documentation }}</h1>

          <div>
            <div>
              <h2>{{ scopedStrings.titles.attributes }}</h2>
              <ul>
                <li
                    v-for="attribute in scopedStrings.attributes"
                    :key="attribute.name"
                >
                  <span class="label">{{ attribute.name }}</span>
                  <span class="description">{{ attribute.description }}</span>
                </li>
              </ul>
            </div>

            <div>
              <h2>{{ scopedStrings.titles.argumentTypes }}</h2>
              <ul>
                <li
                    v-for="argumentType in scopedStrings.argumentTypes"
                    :key="argumentType.name"
                >
                  <span class="label">{{ argumentType.name }}</span>
                  <span class="description">{{ argumentType.description }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div id="overlay-buttons-container">
      <button id="load-changes" @click="loadChanges()">
        {{ scopedStrings.buttons.loadChanges }}
      </button>

      <button id="reload-default" @click="reloadDefault()">
        {{ scopedStrings.buttons.reloadDefault }}
      </button>

      <button id="scroll-up" @click="scrollUp()">
        <i class="fa fa-angle-double-up"></i>
      </button>

      <button id="scroll-down" @click="scrollDown()">
        <i class="fa fa-angle-double-down"></i>
      </button>
    </div>
  </div>
</template>

<script>

import { RawInstruction } from './..///model/rawInstruction'

export default {
  props: {
    scopedStrings: {
      type: Object,
      required: true,
    },
    kernelController: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      kernelDefaultInstructionSet: [],
      currentInstructionSet: [],
    };
  },
  methods: {
    changeDefault(newKernelDefaultInstructionSet) {
      this.checkFormat(newKernelDefaultInstructionSet);
      this.checkTypes(newKernelDefaultInstructionSet);
      // We're working with JSON-serializable data so it's safe to 'clone' the given array this way
      this.kernelDefaultInstructionSet = JSON.parse(
          JSON.stringify(newKernelDefaultInstructionSet)
      );
      this.currentInstructionSet = JSON.parse(
          JSON.stringify(newKernelDefaultInstructionSet)
      );
      this.$emit("instructionSetUpdated", this.getInstructionNames());
    },
    checkFormat(instructionSet) {
      let dummyRawInstruction = new RawInstruction('dummy');
      if (Array.isArray(instructionSet)) {
        for (let i = 0; i < instructionSet.length; i++) {
          for (let key in dummyRawInstruction) {
            if (!Object.hasOwnProperty.call(instructionSet[i], key)) {
              throw new Error(this.scopedStrings.messages.detail.loadJsonNotARawInstructionObject + i + "</b>.");
            }
          }
        }
      } else {
        throw new Error(this.scopedStrings.messages.detail.loadJsonNotAnArray);
      }
    },
    checkTypes(instructionSet) {
      let instructionNameRegex = new RegExp("[a-zA-Z][0-9a-zA-Z_]*");
      let details = this.scopedStrings.messages.detail;
      instructionSet.forEach(function(element, index) {
        if(typeof(element.name) !== "string" || !instructionNameRegex.test(element.name)) {
          throw new Error(`${details.instructionIndex} <b>${index}</b> ${details.invalidName}`);
        }
        if(typeof(element.icode) !== "number") {
          throw new Error(`${element.name} : ${details.field} <b>icode</b> ${details.badFieldType} <b>${details.typeNumber}</b>.`);
        }
        if(typeof(element.ifun) !== "number") {
          throw new Error(`${element.name} : ${details.field} <b>ifun</b> ${details.badFieldType} <b>${details.typeNumber}</b>.`);
        }
        if(typeof(element.args) !== "string") {
          throw new Error(`${element.name} : ${details.field} <b>args</b> ${details.badFieldType} <b>${details.typeString}</b>.`);
        }
      });
    },
    getInstructionNames() {
      let instructionNames = [];
      this.currentInstructionSet.forEach(element => {
        instructionNames.push(element.name)
      });
      // we remove all duplicates within the array by using a set
      let uniqueInstructionNames = [...new Set(instructionNames)];
      return uniqueInstructionNames;
    },
    getInstructionSet() {
      return this.currentInstructionSet;
    },
    setInstructionSet (newInstructionSet) {
      this.checkFormat(newInstructionSet);
      this.currentInstructionSet = newInstructionSet;
    },
    /**
     * Sends the user facing array to the upstream kernel controller.
     * If errors in the input are found, prompt the user with the error message
     * and abort any change made since the last applied changes.
     */
    loadChanges() {
      try {
        this.checkTypes(this.currentInstructionSet);
        this.kernelController.updateInstructionSet(this.currentInstructionSet);
        this.$emit("instructionSetUpdated", this.getInstructionNames());
        this.promptDialog("success", this.scopedStrings.messages.updateSuccess);
      }
      catch (error) {
        this.promptDialog("error", this.scopedStrings.messages.updateError, error.message);
      }
    },
    /**
     * Reloads the default instruction set from the kernel. This will remove
     * any input the user gave.
     */
    reloadDefault() {
      try {
        this.kernelController.updateInstructionSet(this.kernelDefaultInstructionSet);
        this.currentInstructionSet = JSON.parse(JSON.stringify(this.kernelDefaultInstructionSet));
        this.$emit("instructionSetUpdated", this.getInstructionNames());
        this.promptDialog("success", this.scopedStrings.messages.reloadDefaultSuccess);
      } catch (error) {
        this.promptDialog("error", this.scopedStrings.messages.reloadDefaultError, error.message);
      }
    },
    scrollUp() {
      window.scrollTo(document.body.scrollHeight, 0);
    },
    scrollDown() {
      window.scrollTo(0, document.body.scrollHeight);
    },

    /**
     * Adds a new instruction line to the user facing array.
     */
    newInstruction() {
      this.currentInstructionSet.push(new RawInstruction());
      setTimeout(() => this.scrollDown(), 0);
    },
    /**
     * Wrapper to more easily prompt a dialog, the "detail" argument is optional.
     */
    promptDialog(type, message, detail) {
      if (typeof detail === "undefined") {
        this.$emit("showPromptDialog", type, message);
      } else {
        this.$emit("showPromptDialog", type, message, detail);
      }
    },
  },
  computed: {
    defaultInstructionsCount() {
      return this.kernelDefaultInstructionSet.length;
    },
  },
};
</script>

<style scoped>
#instruction-set-editor {
  flex-grow: 0 !important;
}

table {
  border-collapse: collapse;
}

table *:not(i) {
  color: var(--InstructionSet-table-foreground);
  font-size: 12px;
  font-family: monospace;
}

table,
th,
td {
  border: 2px solid var(--InstructionSet-table-border);
}

th {
  padding: 10px 0 8px 0;
  background-color: var(--InstructionSet-table-heading-background);
  color: var(--InstructionSet-table-heading-foreground);
  font-family: monospace;
  font-size: 14px;
}

th,
td {
  width: 120px;
}

td {
  padding: 4px;
}

tr > td:first-child {
  background-color: var(--InstructionSet-table-name-column-background);
  font-weight: 900;
}

tr:last-child > td {
  padding: 0;
}

tr > .remove-instruction {
  width: 0px;
}

tr > .remove-instruction > button {
  position: relative;
  background: none;
  color: var(
      --InstructionSet-table-remove-instruction-button-foreground
  ) !important;
}

tr > .remove-instruction > button > i {
  position: absolute;
  bottom: -4px;
  left: 8px;
  font-size: 18px;
}

td > *:not(span) {
  background-color: var(--InstructionSet-table-background);
}

td > input {
  width: 100%;
  max-width: 118px;
  border: none;
  height: 21px;
  -webkit-appearance: none;
  -moz-appearance: textfield;
}

td > input::placeholder {
  font-weight: 900;
  text-transform: uppercase;
}

td,
td > * {
  text-align: center;
  text-align-last: center;
}

td > select {
  text-shadow: 0 0 0 var(--InstructionSet-table-foreground);
}

td > input:focus,
td > select:focus {
  background-color: var(--InstructionSet-table-focus-background);
}

#new-instruction {
  width: 100%;
}

#documentation > div {
  margin: 20px 20px 0 20px;
  position: -webkit-sticky; /* Safari */
  position: sticky;
  top: 0;
}

h2 {
  font-size: 16px !important;
}

ul {
  padding: 0;
}

ul * {
  font-size: 15px;
}

ul > li {
  display: flex;
  align-items: baseline;
  margin: 16px 0;
  list-style-type: none;
}

ul > li > .description {
  padding-left: 6px;
}

#new-instruction,
button {
  padding: 10px 16px;
  background-color: var(--InstructionSet-button-background);
  color: var(--InstructionSet-button-foreground);
  font-size: 20px;
  transition-duration: 0.3s;
}

#new-instruction:hover,
button:hover {
  background-color: var(--InstructionSet-button-hover-background);
}

#overlay-buttons-container {
  display: grid;
  gap: 4px;
  grid-template-columns: auto auto;
  grid-template-rows: auto auto;
  position: fixed;
  bottom: 4%;
  right: 2%;
}

#overlay-buttons-container > #load-changes {
  align-self: end;
}

#overlay-buttons-container > #reload-default {
  align-self: start;
}

#overlay-buttons-container > #scroll-up,
#overlay-buttons-container > #scroll-down {
  font-size: 32px;
  height: 60px;
  width: 60px;
}

#overlay-buttons-container > #scroll-up {
  border-radius: 100% 100% 0 0;
  grid-column: 2;
  grid-row: 1;
}

#overlay-buttons-container > #scroll-down {
  border-radius: 0 0 100% 100%;
  grid-column: 2;
  grid-row: 2;
}
</style>
